import { useEffect, useMemo, useRef, useState } from "react";
import { Layout } from "@/components/layout";
import {
  useGetMe,
  useGetSettings,
  useUpdateSettings,
  useSyncTicketransferFromAmp,
  useListTicketransferTasks,
  useImportTicketransferTask,
  useDeleteTicketransferTask,
  useRefreshTicketransferTask,
  useResolveTicketransferEvent,
  useListTicketransferTaskLinks,
  getListTicketransferTasksQueryKey,
  getGetSettingsQueryKey,
  type TicketransferTask,
  type TicketransferTaskLink,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  Link2,
  Loader2,
  PanelLeftClose,
  PanelLeftOpen,
  Play,
  RefreshCw,
  Settings2,
  Sparkles,
  Trash2,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { extractArtistFromLabel } from "@/lib/event-grouping";
import { useIsMobile } from "@/hooks/use-mobile";

// ---------------------------------------------------------------------------
// Sidebar persistence keys (separate namespace from workspace sidebar)
// ---------------------------------------------------------------------------
const TT_SIDEBAR_COLLAPSE_KEY = "tex-tt-sidebar-collapsed";
const TT_SIDEBAR_WIDTH_KEY = "tex-tt-sidebar-width";
const DEFAULT_WIDTH = 240;
const MIN_WIDTH = 180;
const MAX_WIDTH = 380;

// ---------------------------------------------------------------------------
// AMP Auto-Sync Settings Panel
// ---------------------------------------------------------------------------
function AmpSyncSettingsPanel() {
  const qc = useQueryClient();
  const { data: settings } = useGetSettings();
  const updateSettings = useUpdateSettings({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getGetSettingsQueryKey() });
        toast.success("Auto-sync settings saved");
      },
      onError: () => toast.error("Failed to save settings"),
    },
  });
  const syncNow = useSyncTicketransferFromAmp({
    mutation: {
      onSuccess: (result) => {
        qc.invalidateQueries({ queryKey: getGetSettingsQueryKey() });
        qc.invalidateQueries({ queryKey: ['/api/ticketransfer/tasks'] });
        const parts: string[] = [];
        if (result.imported > 0) parts.push(`${result.imported} imported`);
        if ((result.rematched ?? 0) > 0) parts.push(`${result.rematched} re-matched`);
        const alreadyOk = Math.max(0, result.found - result.imported - result.errors);
        if (alreadyOk > 0) parts.push(`${alreadyOk} already up-to-date`);
        const totalErrors = result.errors + (result.rematchErrors ?? 0);
        if (totalErrors > 0) parts.push(`${totalErrors} error${totalErrors === 1 ? "" : "s"}`);
        toast.success(`Sync complete${parts.length > 0 ? ` — ${parts.join(", ")}` : ""}`);
      },
      onError: (err: unknown) => {
        const status = (err as { status?: number })?.status;
        if (status === 409) {
          toast.error("A sync is already running — try again shortly");
        } else {
          toast.error("AMP sync failed");
        }
      },
    },
  });

  const tt = settings?.ampTicketransfer;
  const [lookback, setLookback] = useState<string>("");
  const [interval, setInterval] = useState<string>("");

  useEffect(() => {
    if (tt) {
      setLookback(String(tt.lookbackHours ?? 24));
      setInterval(String(tt.intervalMinutes ?? 240));
    }
  }, [tt?.lookbackHours, tt?.intervalMinutes]);

  const saveNumerics = () => {
    const lb = parseInt(lookback, 10);
    const iv = parseInt(interval, 10);
    if (Number.isNaN(lb) || lb < 1 || Number.isNaN(iv) || iv < 10) {
      toast.error("Lookback must be ≥ 1 h and interval must be ≥ 10 min");
      return;
    }
    updateSettings.mutate({
      data: { ampTicketransfer: { lookbackHours: lb, intervalMinutes: iv } },
    });
  };

  const fmtSyncAt = (iso?: string | null) => {
    if (!iso) return "Never";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  };

  return (
    <div className="border border-border rounded bg-panel p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
        <Settings2 className="h-3.5 w-3.5" />
        AMP Auto-Sync
      </h3>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Switch
            id="tt-sync-enabled"
            checked={tt?.syncEnabled ?? false}
            disabled={updateSettings.isPending}
            onCheckedChange={(v) =>
              updateSettings.mutate({
                data: { ampTicketransfer: { syncEnabled: v } },
              })
            }
          />
          <label htmlFor="tt-sync-enabled" className="text-sm cursor-pointer">
            Enable scheduled sync from AMP
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3 max-w-sm">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">
              Lookback (hours)
            </Label>
            <Input
              type="number"
              min={1}
              className="h-7 text-xs"
              value={lookback}
              onChange={(e) => setLookback(e.target.value)}
              onBlur={saveNumerics}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">
              Sync interval (minutes)
            </Label>
            <Input
              type="number"
              min={10}
              className="h-7 text-xs"
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              onBlur={saveNumerics}
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => syncNow.mutate()}
            disabled={syncNow.isPending}
          >
            {syncNow.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Play className="h-3 w-3" />
            )}
            <span className="ml-1">Run Now</span>
          </Button>
          <span className="text-[11px] text-muted-foreground">
            Last sync: {fmtSyncAt(tt?.lastSyncAt)}
            {tt?.lastSyncResult && (
              <>
                {" — "}
                {tt.lastSyncResult.imported} imported
                {(tt.lastSyncResult.rematched ?? 0) > 0 && <>, {tt.lastSyncResult.rematched} re-matched</>}
                {(tt.lastSyncResult.errors + (tt.lastSyncResult.rematchErrors ?? 0)) > 0 && (
                  <>, {tt.lastSyncResult.errors + (tt.lastSyncResult.rematchErrors ?? 0)} err</>
                )}
              </>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------
function fmtDate(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtEventDate(value?: string | null): string {
  if (!value) return "—";
  const hasTime = /\d{2}:\d{2}/.test(value);
  const m = value.match(/(\d{4})-(\d{2})-(\d{2})/);
  let d: Date;
  if (m && !hasTime) {
    d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  } else {
    d = new Date(value);
  }
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function fmtMonthDay(value?: string | null): string | null {
  if (!value) return null;
  const m = value.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return null;
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

const MATCH_LABELS: Record<TicketransferTaskLink["matchMethod"], string> = {
  pos_ticket_id: "Ticket ID",
  barcode: "Barcode",
  section_row_seat: "Section/Row/Seat",
  unmatched: "Unmatched",
};

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------
export default function Ticketransfer() {
  const { data: me } = useGetMe();

  if (me && me.role !== "admin") {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full w-full">
          <p className="text-muted-foreground">
            Ticketransfer links are restricted to administrators.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <TicketransferContent />
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Data types
// ---------------------------------------------------------------------------
export type TaskGroup = {
  key: string | null;
  renderKey: string;
  eventLabel: string | null;
  eventDate: string | null;
  performer: string | null;
  reachEventId: string | null;
  tracked: boolean;
  accountCount: number;
  matched: number;
  total: number;
  tasks: TicketransferTask[];
};

type ArtistSection = {
  artist: string | null;
  groups: TaskGroup[];
};

function eventDateTs(s?: string | null): number | null {
  if (!s) return null;
  const m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

function groupArtist(group: TaskGroup): string | null {
  const p = group.performer?.trim();
  if (p) return p;
  if (group.eventLabel) {
    return extractArtistFromLabel(group.eventLabel, null) ?? group.eventLabel;
  }
  return null;
}

export function groupTasks(tasks: TicketransferTask[]): TaskGroup[] {
  const map = new Map<string, TaskGroup>();
  const accountsByGroup = new Map<string, Set<string>>();
  for (const task of tasks) {
    const key = task.reachEventId ?? task.posEventId ?? null;
    const renderKey = key ?? "__none__";
    let group = map.get(renderKey);
    if (!group) {
      group = {
        key,
        renderKey,
        eventLabel: null,
        eventDate: null,
        performer: null,
        reachEventId: task.reachEventId ?? task.posEventId ?? null,
        tracked: false,
        accountCount: 0,
        matched: 0,
        total: 0,
        tasks: [],
      };
      map.set(renderKey, group);
      accountsByGroup.set(renderKey, new Set());
    }
    group.tasks.push(task);
    group.matched += task.matchedCount;
    group.total += task.linkCount;
    if (task.eventId != null) group.tracked = true;
    if (!group.eventLabel && task.eventLabel) group.eventLabel = task.eventLabel;
    if (!group.eventDate && task.eventDate) group.eventDate = task.eventDate;
    if (!group.performer && task.performer) group.performer = task.performer;
    if (task.accountEmail) accountsByGroup.get(renderKey)!.add(task.accountEmail);
  }
  for (const [renderKey, accounts] of accountsByGroup) {
    map.get(renderKey)!.accountCount = accounts.size;
  }
  return [...map.values()];
}

export function groupByArtistSections(groups: TaskGroup[]): ArtistSection[] {
  const map = new Map<string, ArtistSection>();
  for (const group of groups) {
    const artist = groupArtist(group);
    const key = artist ? artist.toLowerCase() : "__unknown__";
    let section = map.get(key);
    if (!section) {
      section = { artist, groups: [] };
      map.set(key, section);
    }
    section.groups.push(group);
  }

  const lastImport = (g: TaskGroup) =>
    g.tasks.reduce((max, t) => {
      const ts = t.lastImportedAt ? new Date(t.lastImportedAt).getTime() : 0;
      return ts > max ? ts : max;
    }, 0);

  const now = Date.now();
  const sectionSortKey = (s: ArtistSection): { tier: number; value: number } => {
    const dates = s.groups
      .map((g) => eventDateTs(g.eventDate))
      .filter((t): t is number => t !== null);
    const upcoming = dates.filter((t) => t >= now);
    if (upcoming.length > 0) return { tier: 0, value: Math.min(...upcoming) };
    if (dates.length > 0) return { tier: 1, value: -Math.max(...dates) };
    return { tier: 2, value: 0 };
  };

  for (const section of map.values()) {
    if (section.artist === null) {
      section.groups.sort((a, b) => lastImport(b) - lastImport(a));
    } else {
      section.groups.sort((a, b) => {
        const da = eventDateTs(a.eventDate);
        const db = eventDateTs(b.eventDate);
        if (da === null && db === null) return lastImport(b) - lastImport(a);
        if (da === null) return 1;
        if (db === null) return -1;
        return da - db;
      });
    }
  }

  const sections = [...map.values()];
  sections.sort((a, b) => {
    if (a.artist === null) return 1;
    if (b.artist === null) return -1;
    const ka = sectionSortKey(a);
    const kb = sectionSortKey(b);
    if (ka.tier !== kb.tier) return ka.tier - kb.tier;
    if (ka.value !== kb.value) return ka.value - kb.value;
    return a.artist.localeCompare(b.artist);
  });
  return sections;
}

// ---------------------------------------------------------------------------
// Ticketransfer Sidebar
// ---------------------------------------------------------------------------
interface TicketransferSidebarProps {
  groups: TaskGroup[];
  isLoading: boolean;
  selectedKey: string | null;
  onSelectKey: (key: string | null) => void;
  showAll: boolean;
  onToggleShowAll: () => void;
}

function TicketransferSidebar({
  groups,
  isLoading,
  selectedKey,
  onSelectKey,
  showAll,
  onToggleShowAll,
}: TicketransferSidebarProps) {
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(TT_SIDEBAR_COLLAPSE_KEY) === "true";
  });
  const wasMobile = useRef(false);
  useEffect(() => {
    if (isMobile && !wasMobile.current) setCollapsed(true);
    wasMobile.current = isMobile;
  }, [isMobile]);

  const [width, setWidth] = useState<number>(() => {
    if (typeof window === "undefined") return DEFAULT_WIDTH;
    const v = Number(window.localStorage.getItem(TT_SIDEBAR_WIDTH_KEY));
    return v >= MIN_WIDTH && v <= MAX_WIDTH ? v : DEFAULT_WIDTH;
  });
  const [dragging, setDragging] = useState(false);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    window.localStorage.setItem(TT_SIDEBAR_COLLAPSE_KEY, String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    window.localStorage.setItem(TT_SIDEBAR_WIDTH_KEY, String(width));
  }, [width]);

  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(true);
    const startX = e.clientX;
    const startWidth = width;
    const onMove = (ev: PointerEvent) => {
      const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + (ev.clientX - startX)));
      setWidth(next);
    };
    const onUp = () => {
      setDragging(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  };

  const now = Date.now();

  // Split groups into matched (hasMatches), upcoming-no-match, past-no-match
  const matchedGroups = useMemo(
    () => groups.filter((g) => g.matched > 0),
    [groups],
  );
  const zeroMatchGroups = useMemo(
    () => groups.filter((g) => g.matched === 0),
    [groups],
  );

  // For matched groups, split upcoming vs past
  const { upcomingMatched, pastMatched } = useMemo(() => {
    const upcoming: TaskGroup[] = [];
    const past: TaskGroup[] = [];
    for (const g of matchedGroups) {
      const ts = eventDateTs(g.eventDate);
      if (ts !== null && ts < now) past.push(g);
      else upcoming.push(g);
    }
    // Sort upcoming nearest-date first; nulls last
    upcoming.sort((a, b) => {
      const da = eventDateTs(a.eventDate);
      const db = eventDateTs(b.eventDate);
      if (da === null && db === null) return 0;
      if (da === null) return 1;
      if (db === null) return -1;
      return da - db;
    });
    // Sort past most-recent first
    past.sort((a, b) => {
      const da = eventDateTs(a.eventDate);
      const db = eventDateTs(b.eventDate);
      if (da === null && db === null) return 0;
      if (da === null) return 1;
      if (db === null) return -1;
      return db - da;
    });
    return { upcomingMatched: upcoming, pastMatched: past };
  }, [matchedGroups, now]);

  const totalVisible = matchedGroups.length;

  const renderGroupRow = (group: TaskGroup) => {
    const active = selectedKey === group.renderKey;
    const monthDay = fmtMonthDay(group.eventDate);
    const fullyMatched = group.total > 0 && group.matched === group.total;
    const hasAttention = group.key && (!group.tracked || (group.total > 0 && group.matched < group.total));
    return (
      <button
        key={group.renderKey}
        onClick={() => onSelectKey(active ? null : group.renderKey)}
        aria-current={active ? "true" : undefined}
        className={cn(
          "w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md border text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          active
            ? "bg-secondary border-border shadow-sm"
            : "bg-transparent border-transparent hover:bg-muted hover:border-border",
        )}
      >
        <span className="shrink-0 w-[3.75rem] leading-tight">
          <span className="text-[10px] font-mono font-medium text-muted-foreground">
            {monthDay ?? "—"}
          </span>
        </span>
        <span className="flex-1 truncate text-xs text-foreground">
          {group.eventLabel ?? group.reachEventId ?? "Unknown"}
        </span>
        {group.matched > 0 ? (
          fullyMatched ? (
            <span className="shrink-0 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-mono font-medium text-emerald-400 tabular-nums">
              {group.matched}
            </span>
          ) : (
            <span
              className={cn(
                "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-mono font-medium tabular-nums",
                hasAttention
                  ? "bg-amber-500/15 text-amber-400"
                  : "bg-primary/15 text-primary",
              )}
            >
              {group.matched}/{group.total}
            </span>
          )
        ) : (
          <span className="shrink-0 px-1.5 text-[10px] font-mono text-muted-foreground/40 tabular-nums">
            0
          </span>
        )}
      </button>
    );
  };

  if (collapsed) {
    return (
      <aside className="relative border-r border-border bg-card flex flex-col h-full shrink-0 w-12 overflow-hidden">
        <div className="h-[49px] border-b border-border flex items-center justify-center shrink-0">
          <button
            onClick={() => setCollapsed(false)}
            className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Expand Ticketransfer sidebar"
            title="Expand"
          >
            <PanelLeftOpen className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-2 px-1 space-y-1.5 custom-scrollbar">
          {isLoading ? (
            <div className="flex justify-center p-2">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            </div>
          ) : (
            groups.filter((g) => showAll || g.matched > 0).map((group) => {
              const active = selectedKey === group.renderKey;
              const monthDay = fmtMonthDay(group.eventDate);
              const label = group.eventLabel ?? group.reachEventId ?? "Unknown";
              return (
                <button
                  key={group.renderKey}
                  onClick={() => onSelectKey(active ? null : group.renderKey)}
                  aria-label={label}
                  aria-current={active ? "true" : undefined}
                  className="flex flex-col items-center gap-1 w-full py-0.5 focus-visible:outline-none group"
                >
                  <span
                    className={cn(
                      "relative w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-semibold transition-all ring-offset-2 ring-offset-card group-focus-visible:ring-2 group-focus-visible:ring-ring",
                      active
                        ? "bg-secondary text-foreground ring-2 ring-primary shadow-sm"
                        : "bg-muted text-muted-foreground group-hover:bg-secondary group-hover:text-foreground",
                    )}
                  >
                    {label.slice(0, 2).toUpperCase()}
                    {group.matched > 0 && (
                      <span
                        className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary ring-2 ring-card"
                        aria-hidden="true"
                      />
                    )}
                  </span>
                  <span className="text-[8px] font-mono uppercase tracking-wide text-muted-foreground leading-none">
                    {monthDay ?? "—"}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </aside>
    );
  }

  return (
    <aside
      className={cn(
        "relative border-r border-border bg-card flex flex-col h-full shrink-0 overflow-hidden",
        !dragging && "transition-[width] duration-200 ease-in-out",
      )}
      style={{ width }}
    >
      {/* Resize handle */}
      <div
        onPointerDown={startResize}
        onDoubleClick={() => setWidth(DEFAULT_WIDTH)}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar (double-click to reset)"
        title="Drag to resize · double-click to reset"
        className="group absolute top-0 right-0 z-20 h-full w-1.5 cursor-col-resize"
      >
        <span
          className={cn(
            "absolute inset-y-0 right-0 w-px transition-colors",
            dragging ? "bg-primary" : "bg-transparent group-hover:bg-primary/50",
          )}
        />
      </div>

      {/* Header */}
      <div className="h-[49px] px-3 border-b border-border flex items-center justify-between bg-card shrink-0">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Events
        </h2>
        <button
          onClick={() => setCollapsed(true)}
          className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Collapse sidebar"
          title="Collapse"
        >
          <PanelLeftClose className="h-4 w-4" />
        </button>
      </div>

      {/* Scrollable list */}
      <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
        {isLoading ? (
          <div className="flex justify-center p-4">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          </div>
        ) : totalVisible === 0 && !showAll ? (
          <div className="p-4 text-center text-xs text-muted-foreground">
            No matched events yet.
          </div>
        ) : (
          <div className="space-y-1">
            {/* Upcoming matched groups */}
            {upcomingMatched.length > 0 && (
              <div className="space-y-0.5">
                {upcomingMatched.map(renderGroupRow)}
              </div>
            )}

            {/* Past matched groups behind Archived disclosure */}
            {pastMatched.length > 0 && (
              <div className="pt-1">
                <button
                  onClick={() => setShowPast((s) => !s)}
                  className="w-full text-center text-[11px] text-muted-foreground/70 hover:text-foreground py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                >
                  {showPast
                    ? "Hide archived"
                    : `${pastMatched.length} archived event${pastMatched.length === 1 ? "" : "s"}`}
                </button>
                {showPast && (
                  <div className="mt-1 space-y-0.5">
                    <div className="px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/60">
                      Archived
                    </div>
                    {pastMatched.map(renderGroupRow)}
                  </div>
                )}
              </div>
            )}

            {/* Zero-match groups (only when showAll) */}
            {showAll && zeroMatchGroups.length > 0 && (
              <div className="pt-1 space-y-0.5">
                <div className="px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/60">
                  No matches yet
                </div>
                {zeroMatchGroups.map(renderGroupRow)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer — show/hide zero-match toggle + count */}
      <div className="px-3 py-2 border-t border-border bg-panel shrink-0 flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium text-muted-foreground whitespace-nowrap">
          {totalVisible} event{totalVisible === 1 ? "" : "s"}
        </span>
        {zeroMatchGroups.length > 0 && (
          <button
            onClick={onToggleShowAll}
            className="text-[11px] text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            {showAll ? "Hide unmatched" : `+${zeroMatchGroups.length} unmatched`}
          </button>
        )}
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Main content
// ---------------------------------------------------------------------------
function TicketransferContent() {
  const qc = useQueryClient();

  // Sidebar selection & showAll state (declared early so the two queries can
  // reference showAll when deciding whether to enable)
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  // Two separate queries so zero-match events are never fetched until the
  // operator toggles "Show all". `matchedQuery` is always enabled and drives
  // the initial sidebar + right-pane view. `allQuery` is enabled lazily only
  // when showAll=true and includes ALL tasks (matched + unmatched).
  const matchedQuery = useListTicketransferTasks({ matchedOnly: true });
  const allQuery = useListTicketransferTasks(
    undefined,
    { query: { enabled: showAll, queryKey: getListTicketransferTasksQueryKey(undefined) } },
  );

  const activeTasks = showAll
    ? (allQuery.data ?? matchedQuery.data ?? [])
    : (matchedQuery.data ?? []);
  const isLoading = showAll ? allQuery.isLoading : matchedQuery.isLoading;

  // Invalidate both query variants so either view refreshes after a mutation.
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['/api/ticketransfer/tasks'] });

  const groups = useMemo(() => groupTasks(activeTasks), [activeTasks]);

  // When the selected key disappears from the group list (e.g. after delete),
  // clear the selection so the right pane doesn't stay blank.
  useEffect(() => {
    if (selectedKey !== null && !groups.some((g) => g.renderKey === selectedKey)) {
      setSelectedKey(null);
    }
  }, [groups, selectedKey]);

  // Groups visible in the right pane:
  // - If a group is selected, show only that group.
  // - Otherwise show all groups that pass the showAll filter.
  const visibleGroups = useMemo(() => {
    if (selectedKey !== null) {
      return groups.filter((g) => g.renderKey === selectedKey);
    }
    if (!showAll) {
      return groups.filter((g) => g.matched > 0);
    }
    return groups;
  }, [groups, selectedKey, showAll]);

  // Section grouping for the right pane
  const sections = useMemo(() => groupByArtistSections(visibleGroups), [visibleGroups]);
  const showArtistHeaders = useMemo(
    () => sections.filter((s) => s.artist !== null).length > 1,
    [sections],
  );

  const [taskInput, setTaskInput] = useState("");

  const parseTaskIds = (raw: string): string[] => [
    ...new Set(
      raw
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ];

  const importTask = useImportTicketransferTask({
    mutation: {
      onSuccess: (tasks) => {
        invalidate();
        setTaskInput("");
        const errored = tasks.filter((t) => t.importStatus === "error");
        const matched = tasks.reduce((n, t) => n + t.matchedCount, 0);
        const links = tasks.reduce((n, t) => n + t.linkCount, 0);
        if (tasks.length > 0 && errored.length === tasks.length) {
          toast.error(
            `All ${tasks.length} task${tasks.length === 1 ? "" : "s"} failed to import`,
          );
        } else {
          toast.success(
            `Imported ${tasks.length - errored.length}/${tasks.length} task${tasks.length === 1 ? "" : "s"} · ${matched}/${links} link${links === 1 ? "" : "s"} matched`,
          );
          if (errored.length > 0) {
            toast.error(
              `${errored.length} task${errored.length === 1 ? "" : "s"} failed — see the table for details`,
            );
          }
        }
      },
      onError: () => toast.error("Failed to import tasks"),
    },
  });

  const submit = () => {
    const ids = parseTaskIds(taskInput);
    if (ids.length === 0) {
      toast.error("Enter at least one Ticketransfer task ID");
      return;
    }
    importTask.mutate({ data: { taskIds: ids } });
  };

  return (
    <div className="h-full flex overflow-hidden">
      {/* Left sidebar */}
      <TicketransferSidebar
        groups={groups}
        isLoading={isLoading}
        selectedKey={selectedKey}
        onSelectKey={setSelectedKey}
        showAll={showAll}
        onToggleShowAll={() => setShowAll((s) => !s)}
      />

      {/* Right scrollable pane */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="p-4 sm:p-6 space-y-5 max-w-[1100px] mx-auto">
          {/* Page header */}
          <div>
            <h1 className="text-lg font-semibold tracking-tight flex items-center gap-2">
              <Link2 className="h-4 w-4 text-sky-400" />
              Ticketransfer Links
            </h1>
            <p className="text-xs text-muted-foreground mt-1">
              Import Ticketransfer tasks to attach per-ticket delivery links to
              domestic listings. Select an event in the sidebar to focus it, or
              browse all matched events below.
            </p>
          </div>

          <AmpSyncSettingsPanel />

          {/* Import panel */}
          <div className="border border-border rounded bg-panel p-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Import Tasks
            </h3>
            <div className="space-y-2">
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">
                  Ticketransfer Task IDs
                </Label>
                <Textarea
                  className="min-h-[72px] w-full max-w-md font-mono text-xs"
                  value={taskInput}
                  onChange={(e) => setTaskInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
                  }}
                  placeholder={"One task ID per line, e.g.\n84213\n84214"}
                />
                <p className="text-[10px] text-muted-foreground">
                  Paste a newline-separated list to import several at once. Press
                  ⌘/Ctrl+Enter to import.
                </p>
              </div>
              <Button
                size="sm"
                className="h-8"
                onClick={submit}
                disabled={importTask.isPending}
              >
                {importTask.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                <span className="ml-1">Import</span>
              </Button>
            </div>
          </div>

          {/* Event group cards */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : groups.length === 0 ? (
            <div className="border border-border rounded py-8 text-center text-xs text-muted-foreground">
              No Ticketransfer tasks imported yet.
            </div>
          ) : visibleGroups.length === 0 ? (
            <div className="border border-border rounded py-8 text-center text-xs text-muted-foreground">
              {selectedKey !== null
                ? "Selected event not found."
                : "No events with matches yet. Use the sidebar toggle to show all events."}
            </div>
          ) : (
            <>
              {/* When a single event is selected, show a back link */}
              {selectedKey !== null && (
                <div>
                  <button
                    onClick={() => setSelectedKey(null)}
                    className="text-[11px] text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded flex items-center gap-1"
                  >
                    <ChevronRight className="h-3 w-3 rotate-180" />
                    Show all events
                  </button>
                </div>
              )}
              <div className="space-y-5">
                {sections.map((section) => (
                  <ArtistSectionView
                    key={section.artist ?? "__unknown__"}
                    section={section}
                    showHeader={showArtistHeaders}
                    selectedKey={selectedKey}
                    onMutated={invalidate}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Artist section view
// ---------------------------------------------------------------------------
function ArtistSectionView({
  section,
  showHeader,
  selectedKey,
  onMutated,
}: {
  section: ArtistSection;
  showHeader: boolean;
  selectedKey: string | null;
  onMutated: () => void;
}) {
  const cards = (
    <div className="space-y-3">
      {section.groups.map((group) => (
        <GroupCard
          key={group.renderKey}
          group={group}
          isSelected={selectedKey === group.renderKey}
          onMutated={onMutated}
        />
      ))}
    </div>
  );

  const headerText =
    section.artist ?? (section.groups.length > 0 ? "Unknown event" : null);
  const showThisHeader = section.artist !== null ? showHeader : true;

  if (!showThisHeader || !headerText) return cards;

  return (
    <div className="space-y-2">
      <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-1">
        {headerText}
      </h2>
      {cards}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Group card — collapsed by default, auto-expanded when selected
// ---------------------------------------------------------------------------
function GroupCard({
  group,
  isSelected,
  onMutated,
}: {
  group: TaskGroup;
  isSelected: boolean;
  onMutated: () => void;
}) {
  // Auto-expand when the sidebar selects this group. Toggling the header
  // still lets the operator collapse it again manually.
  const [expanded, setExpanded] = useState(isSelected);
  useEffect(() => {
    if (isSelected) setExpanded(true);
  }, [isSelected]);

  const needsAttention =
    !!group.key &&
    (!group.tracked || (group.total > 0 && group.matched < group.total));
  const fullyMatched = group.total > 0 && group.matched === group.total;

  const resolve = useResolveTicketransferEvent({
    mutation: {
      onSuccess: (res) => {
        onMutated();
        const matched = res.tasks.reduce((n, t) => n + t.matchedCount, 0);
        const total = res.tasks.reduce((n, t) => n + t.linkCount, 0);
        const errored = res.tasks.filter((t) => t.importStatus === "error");
        if (errored.length === res.tasks.length && res.tasks.length > 0) {
          toast.error(
            `${res.event.label}: tasks failed to re-import — Reach may be unavailable`,
          );
        } else {
          toast.success(
            `${res.event.label} ${res.event.created ? "added" : "refreshed"} · ${matched}/${total} link${total === 1 ? "" : "s"} matched`,
          );
        }
      },
      onError: () =>
        toast.error(
          "Failed to resolve event — Reach may be unavailable. Try again.",
        ),
    },
  });

  const steps = group.tracked
    ? ["Pulling from Reach…", "Matching tickets…"]
    : ["Adding event…", "Pulling from Reach…", "Matching tickets…"];
  const [stepIdx, setStepIdx] = useState(0);
  useEffect(() => {
    if (!resolve.isPending) {
      setStepIdx(0);
      return;
    }
    const t = setInterval(
      () => setStepIdx((i) => Math.min(i + 1, steps.length - 1)),
      1300,
    );
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolve.isPending]);

  const buttonLabel = resolve.isPending
    ? steps[stepIdx]
    : group.tracked
      ? "Pull from Reach & match"
      : "Add event & pull from Reach";

  const headerTitle = group.eventLabel ? (
    <span className="font-medium">{group.eventLabel}</span>
  ) : group.reachEventId ? (
    <span className="font-mono text-muted-foreground">{group.reachEventId}</span>
  ) : (
    <span className="text-muted-foreground">Unknown event</span>
  );

  const accountsText = `${group.accountCount} account${group.accountCount === 1 ? "" : "s"}`;

  return (
    <div
      className={cn(
        "border rounded overflow-hidden",
        needsAttention ? "border-amber-500/50 bg-amber-500/5" : "border-border",
        isSelected && "ring-1 ring-primary/40",
      )}
    >
      {/* Collapsible header — clicking toggles the task table */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-3 py-2.5 bg-panel/60 hover:bg-panel transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
        aria-expanded={expanded}
      >
        <div className="min-w-0 flex items-start gap-2">
          <span className="mt-0.5 shrink-0">
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm">
              {needsAttention ? (
                <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" />
              ) : fullyMatched ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
              ) : (
                <Link2 className="h-3.5 w-3.5 text-sky-400 shrink-0" />
              )}
              <span className="truncate">{headerTitle}</span>
              {!group.tracked && group.key && (
                <Badge
                  variant="outline"
                  className="text-[10px] border-amber-500/40 text-amber-300"
                >
                  Not tracked
                </Badge>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
              {group.eventDate ? `${fmtEventDate(group.eventDate)} · ` : ""}
              {accountsText} · {group.tasks.length} task
              {group.tasks.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-6 sm:ml-0">
          <div className="text-right">
            <div
              className={cn(
                "text-sm font-mono",
                fullyMatched
                  ? "text-emerald-400"
                  : needsAttention
                    ? "text-amber-300"
                    : "text-foreground",
              )}
            >
              {group.matched}/{group.total}
            </div>
            <div className="text-[10px] text-muted-foreground">
              {fullyMatched
                ? "matched"
                : group.total === 0
                  ? "no links"
                  : group.matched === 0
                    ? "unmatched"
                    : "partial"}
            </div>
          </div>
          {needsAttention && (
            <Button
              size="sm"
              className="h-8 bg-amber-500 hover:bg-amber-400 text-amber-950"
              onClick={(e) => {
                e.stopPropagation();
                group.key && resolve.mutate({ data: { eventKey: group.key } });
              }}
              disabled={resolve.isPending}
            >
              {resolve.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              <span className="ml-1.5">{buttonLabel}</span>
            </Button>
          )}
        </div>
      </button>

      {/* Expandable task table */}
      {expanded && (
        <div className="overflow-x-auto custom-scrollbar border-t border-border/60">
          <table className="w-full text-xs min-w-[640px]">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="w-8 py-1.5 px-2" />
                <th className="text-left py-1.5 px-3 font-medium">Task ID</th>
                <th className="text-left py-1.5 px-3 font-medium">Account</th>
                <th className="text-right py-1.5 px-3 font-medium">Matched</th>
                <th className="text-left py-1.5 px-3 font-medium">Status</th>
                <th className="text-left py-1.5 px-3 font-medium">Imported</th>
                <th className="text-right py-1.5 px-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {group.tasks.map((task) => (
                <TaskRow key={task.id} task={task} onMutated={onMutated} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Task row (individual task inside an expanded group card)
// ---------------------------------------------------------------------------
function TaskRow({
  task,
  onMutated,
}: {
  task: TicketransferTask;
  onMutated: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const refresh = useRefreshTicketransferTask({
    mutation: {
      onSuccess: (t) => {
        onMutated();
        if (t.importStatus === "error") {
          toast.error(
            `Refreshed with errors${t.importError ? `: ${t.importError}` : ""}`,
          );
        } else {
          toast.success(
            `Refreshed · ${t.matchedCount}/${t.linkCount} link${t.linkCount === 1 ? "" : "s"} matched`,
          );
        }
      },
      onError: () => toast.error("Failed to refresh task"),
    },
  });

  const del = useDeleteTicketransferTask({
    mutation: {
      onSuccess: () => {
        onMutated();
        toast.success("Task deleted");
      },
      onError: () => toast.error("Failed to delete task"),
    },
  });

  const busy = refresh.isPending || del.isPending;

  return (
    <>
      <tr className="border-t border-border hover:bg-muted/40">
        <td className="py-1.5 px-2 text-center">
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        </td>
        <td className="py-1.5 px-3 font-mono">{task.taskId}</td>
        <td className="py-1.5 px-3 text-muted-foreground">
          {task.accountEmail || "—"}
        </td>
        <td className="py-1.5 px-3 text-right font-mono">
          {task.matchedCount}/{task.linkCount}
        </td>
        <td className="py-1.5 px-3">
          {task.importStatus === "error" ? (
            <Badge
              variant="destructive"
              className="text-[10px]"
              title={task.importError ?? undefined}
            >
              Error
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px]">
              Imported
            </Badge>
          )}
        </td>
        <td className="py-1.5 px-3 text-muted-foreground whitespace-nowrap">
          {fmtDate(task.lastImportedAt)}
          {task.importedBy ? (
            <span className="block text-[10px] opacity-70">{task.importedBy}</span>
          ) : null}
        </td>
        <td className="py-1.5 px-3">
          <div className="flex items-center justify-end gap-1">
            <Button
              size="sm"
              variant="outline"
              className="h-7 rounded-full"
              onClick={() => refresh.mutate({ id: task.id })}
              disabled={busy}
            >
              {refresh.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              <span className="ml-1">Refresh</span>
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-full text-red-400 hover:text-red-300"
                  disabled={busy}
                  aria-label="Delete task"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this task?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This removes task {task.taskId} and its {task.linkCount}{" "}
                    stored link{task.linkCount === 1 ? "" : "s"}. Affected
                    listings will lose their Ticketransfer links until
                    re-imported.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => del.mutate({ id: task.id })}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-border/50 bg-background/40">
          <td colSpan={7} className="px-3 py-2">
            <TaskLinks taskId={task.id} />
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Task links (expanded row detail)
// ---------------------------------------------------------------------------
function TaskLinks({ taskId }: { taskId: number }) {
  const { data: links, isLoading } = useListTicketransferTaskLinks(taskId);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading links…
      </div>
    );
  }

  if (!links || links.length === 0) {
    return (
      <p className="py-2 text-xs text-muted-foreground">
        No links found for this task.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto custom-scrollbar">
      <table className="w-full text-[11px] min-w-[920px]">
        <thead className="text-muted-foreground">
          <tr>
            <th className="text-left py-1 px-2 font-medium">Section</th>
            <th className="text-left py-1 px-2 font-medium">Row</th>
            <th className="text-left py-1 px-2 font-medium">Seat</th>
            <th className="text-left py-1 px-2 font-medium">Reach Ticket</th>
            <th className="text-left py-1 px-2 font-medium">Listing</th>
            <th className="text-left py-1 px-2 font-medium">Purchase</th>
            <th className="text-left py-1 px-2 font-medium">Invoice</th>
            <th className="text-left py-1 px-2 font-medium">URL</th>
            <th className="text-left py-1 px-2 font-medium">Match</th>
          </tr>
        </thead>
        <tbody>
          {links.map((link) => (
            <tr key={link.id} className="border-t border-border/40">
              <td className="py-1 px-2">{link.section || "—"}</td>
              <td className="py-1 px-2">{link.row || "—"}</td>
              <td className="py-1 px-2">{link.seat || "—"}</td>
              <td className="py-1 px-2 font-mono text-muted-foreground">
                {link.reachTicketId || "—"}
              </td>
              <td className="py-1 px-2 font-mono text-muted-foreground">
                {link.reachListingId || "—"}
              </td>
              <td className="py-1 px-2 font-mono text-muted-foreground">
                {link.posPurchaseId || "—"}
              </td>
              <td className="py-1 px-2 font-mono text-muted-foreground">
                {link.posInvoiceId || "—"}
              </td>
              <td className="py-1 px-2">
                {link.url ? (
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sky-400 hover:text-sky-300 hover:underline"
                    title="Open delivery link"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Open
                  </a>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
              <td className="py-1 px-2">
                {link.matchMethod === "unmatched" ? (
                  <span className="text-amber-400">
                    {MATCH_LABELS[link.matchMethod]}
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    {MATCH_LABELS[link.matchMethod]}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
